/* =============================================================================
   VIEWS / ADMIN / DELIVERY — Liefer-Cockpit (Session 62, Phase 64)
   =============================================================================
   Vollbild-Disponent-View für die Lieferungen. Parallel zu KDS (Küche) und
   Bar (Theke) als 4. operatives Display. Adress-Block + Fahrer-Zuweisung +
   Status-Lifecycle (pending → assigned → picked_up → out_for_delivery →
   delivered).

   Aufgerufen über:
     - Hash '#delivery' (per view-switch.js → switchView('delivery'))
     - Cmd/Ctrl+Shift+L (in bindAdminShortcut registriert)
     - Topbar-Pill „🛵 Lieferung" in admin-shell.js
     - Cockpit-Triade-Kachel im Dashboard (S62)

   Realtime:
     - Wir nutzen App.events 'order:realtime', das die orders-Tab schon
       abonniert. Delivery-spezifisch: Re-Render bei jeder Änderung.
     - body.classList 'delivery-active' damit Cockpit-Notifications
       nicht doppelt anschlagen.

   Layout:
     - Topbar mit Status-Counters + Filter
     - 4 Spalten/Buckets:
         NEU/Eingehend (pending)      = Order noch nicht zugewiesen
         ZUGEWIESEN (assigned)         = Fahrer benannt, noch nicht abgeholt
         ABGEHOLT (picked_up)          = Fahrer hat Order entgegengenommen
         UNTERWEGS (out_for_delivery)  = Fahrer auf der Tour
       (DELIVERED = terminal, in Cockpit-Bestellungen-Archiv-Pfad)

   Exporte:
     renderDelivery()                  — Haupt-Renderer für #view-delivery
     enterDeliveryView() / exitDeliveryView()
     deliveryAssignDriver(orderId, driverId)
     deliveryCancelDelivery(orderId)
   S212: Status/Kassieren treibt jetzt das Fahrer-Terminal — das Cockpit ist
   read-only (Fortschritts-/Kassier-Buttons entfernt), Zuweisung + Storno bleiben.
============================================================================ */

import { htmlEscape, showToast, formatCustomizationsByCategory } from '../../utils/helpers.js';
import {
  itemsFor, hasCachedItems, fetchItemsFor, batchFetchItemsFor
} from '../../core/order-items-cache.js';
import { renderOperatorBadges } from './operator-ui.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [], settings: {} };
}

// -----------------------------------------------------------------------------
// Items-Cache — S104-B: gemeinsames Item-Access-Modul (core/order-items-cache.js).
// Ersetzt den Liefer-eigenen `_itemsCache`. EINE Quelle für alle Displays.
// -----------------------------------------------------------------------------

/** Liefert Items synchron: order_items (Cache) gewinnt, sonst preorderSnapshot-
 *  Fallback (`itemsFor`). Startet bei Cache-Miss einen Hintergrund-Fetch +
 *  Re-Render (nächster Render zeigt die vollen DB-Items mit customizations). */
function itemsForRender(order) {
  if (!order) return [];
  const items = itemsFor(order);
  if (!hasCachedItems(order.id)) {
    fetchItemsFor(order.id).then(() => {
      if (document.body.classList.contains('delivery-active')) renderDelivery();
    });
  }
  return items;
}

// -----------------------------------------------------------------------------
// Drivers-Cache — geladen on-enter + bei Realtime-Events refreshed.
// -----------------------------------------------------------------------------
let _drivers = [];
let _driversLoadedAt = 0;

// S209/phase_156 — Fahrer sind jetzt User mit Rolle `driver` (Operator-Layer).
// Der Dropdown liest über list_assignable_drivers { id, name, phone, busy_count };
// „unterwegs (N)" wird aus offenen Orders abgeleitet (kein drivers.status mehr).
async function loadDrivers(force = false) {
  const age = Date.now() - _driversLoadedAt;
  if (!force && _drivers.length > 0 && age < 30000) return _drivers;
  try {
    const storage = window.App && window.App.storage && window.App.storage.drivers;
    if (!storage || typeof storage.listAssignable !== 'function') return [];
    const rows = await storage.listAssignable();
    _drivers = sortAssignableDrivers(rows);
    _driversLoadedAt = Date.now();
  } catch (e) {
    console.warn('[delivery] loadDrivers failed:', e);
    _drivers = [];
  }
  return _drivers;
}

/** Pure (Vitest): Sortierung fürs Cockpit-Dropdown — freie Fahrer zuerst
 *  (busy_count 0), dann die „unterwegs" nach Auslastung, dann alphabetisch. */
export function sortAssignableDrivers(rows) {
  return [...(rows || [])].sort((a, b) => {
    const ba = Number(a && a.busy_count) || 0;
    const bb = Number(b && b.busy_count) || 0;
    if (ba !== bb) return ba - bb;
    return String((a && a.name) || '').localeCompare(String((b && b.name) || ''), 'de');
  });
}

function getDriverById(id) {
  if (!id) return null;
  return _drivers.find(d => d.id === id) || null;
}

// -----------------------------------------------------------------------------
// Filter + Buckets
// -----------------------------------------------------------------------------

// S62 HF7 / S98 — 3-Bucket-Lifecycle (Jonas-Direktive). Konsistent zum KDS-
// Flow: 3 Klicks. „Ausgeliefert + Bezahlt" ist terminal und räumt die Order
// SOFORT aus der Sicht (Verhaltens-Gleichstand mit KDS/Theke `picked_up`) —
// keine „Heute ausgeliefert"-Tages-Spalte mehr. Historie liegt im Archiv-Tab
// (Single-Source).
const DELIVERY_BUCKETS = [
  { id: 'pending',          label: 'Neu',             icon: '📥', sub: 'Noch nicht zugewiesen' },
  { id: 'assigned',         label: 'Zugewiesen',      icon: '🧑‍🍳', sub: 'Fahrer benannt, wartet auf Küche/Tour-Start' },
  { id: 'out_for_delivery', label: 'In Auslieferung', icon: '🛵', sub: 'Fahrer auf Tour zum Kunden' }
];

// Naming kongruent zum KDS-Pattern: jedes Label ist die ZIEL-Aktion, nicht der
// erreichte Status. So sieht der Operator was er als nächstes klickt.
const NEXT_STATUS = {
  pending:          { next: 'assigned',         label: '✓ Zuweisen' },
  assigned:         { next: 'out_for_delivery', label: '🛵 In Auslieferung' },
  out_for_delivery: { next: 'delivered',        label: '✅ Ausgeliefert + Bezahlt' }
};

/** Alle AKTIVEN Liefer-Orders. S98 — `delivered` fällt sofort aus der Sicht
 *  (Verhaltens-Gleichstand mit KDS/Theke `picked_up`); cancelled/no_show
 *  ebenso. Keine „Heute ausgeliefert"-Tages-Spalte mehr — die Historie steht
 *  im Archiv-Tab (Single-Source). Der Re-Render nach „Ausgeliefert + Bezahlt"
 *  passiert über `order:status_changed`/`renderDelivery()`. */
export function getDeliveryOrders() {
  const state = getState();
  const orders = Array.isArray(state.orders) ? state.orders : [];
  return orders.filter(o => {
    if (!o) return false;
    if (o.serviceType !== 'delivery') return false;
    if (o.status === 'cancelled' || o.status === 'no_show') return false;
    if (o.deliveryStatus === 'delivered') return false;
    return true;
  });
}

function bucketOf(order) {
  const s = order.deliveryStatus || 'pending';
  if (DELIVERY_BUCKETS.find(b => b.id === s)) return s;
  return 'pending';
}

function isPreOrder(order, nowMs = Date.now()) {
  if (!order || !order.pickupDate || !order.pickupTime) return false;
  const iso = `${order.pickupDate}T${order.pickupTime}`;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return false;
  return t > nowMs + 60 * 60 * 1000; // > 60 Min in Zukunft
}

function isHot(order, nowMs = Date.now()) {
  if (!order || !order.pickupDate || !order.pickupTime) return true;
  const iso = `${order.pickupDate}T${order.pickupTime}`;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return true;
  return t <= nowMs + 60 * 60 * 1000;
}

// -----------------------------------------------------------------------------
// Counters für die Topbar
// -----------------------------------------------------------------------------

export function getDeliveryCounts(orders) {
  // S62 HF7 / S98 — 3-Bucket-Logik (Jonas-Direktive). picked_up nicht mehr
  // im Lifecycle; delivered fällt sofort aus der Sicht (keine Tages-Spalte).
  const counts = { pending: 0, assigned: 0, out_for_delivery: 0, total: 0, preOrder: 0 };
  const now = Date.now();
  for (const o of (orders || [])) {
    const b = bucketOf(o);
    counts[b] = (counts[b] || 0) + 1;
    counts.total++;
    if (isPreOrder(o, now)) counts.preOrder++;
  }
  return counts;
}

// -----------------------------------------------------------------------------
// Tile-Rendering pro Order
// -----------------------------------------------------------------------------

function fmtAddress(addr, customerName) {
  if (!addr || typeof addr !== 'object') return '<span class="delivery-addr-empty">⚠️ Adresse fehlt</span>';
  const street = addr.street || '';
  const plz    = addr.plz    || '';
  const city   = addr.city   || '';
  const floor  = addr.floor  || '';
  const notes  = addr.driver_notes || addr.notes || '';
  const zone   = addr.zone_name || '';
  // Maps-Link für die Navigation. Universal Maps-URL (öffnet Google Maps in
  // Browser oder native Maps-App auf iOS, je nach OS).
  const mapsQuery = encodeURIComponent(`${street}, ${plz} ${city}, Deutschland`.replace(/\s+/g, ' ').trim());
  const mapsUrl   = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;
  return `
    <div class="delivery-addr">
      <div class="delivery-addr-row delivery-addr-street">
        <strong>${htmlEscape(street || '⚠️ Straße fehlt')}</strong>
      </div>
      <div class="delivery-addr-row delivery-addr-place">
        <strong>${htmlEscape(plz)}</strong> ${htmlEscape(city)}
        ${zone ? `<span class="delivery-addr-zone">${htmlEscape(zone)}</span>` : ''}
      </div>
      ${floor ? `<div class="delivery-addr-row delivery-addr-floor">🏢 Etage: <strong>${htmlEscape(floor)}</strong></div>` : ''}
      ${customerName ? `<div class="delivery-addr-row delivery-addr-bell">🔔 Klingel: <strong>${htmlEscape(customerName)}</strong></div>` : ''}
      ${notes ? `<div class="delivery-addr-row delivery-addr-notes">📝 <strong>Hinweis:</strong> ${htmlEscape(notes)}</div>` : ''}
      <a class="delivery-addr-maps" href="${htmlEscape(mapsUrl)}" target="_blank" rel="noopener" onclick="event.stopPropagation()">
        🗺️ In Maps öffnen
      </a>
    </div>
  `;
}

/** Item-Liste für den Fahrer: Menge + Name + Customizations + Notes.
 *  Drinks/Speisen werden visuell unterschieden, damit der Fahrer beim Picken
 *  abhaken kann ob alles dabei ist.
 */
function fmtItems(items) {
  if (!items || items.length === 0) {
    return `<div class="delivery-items-empty">⚠️ Keine Items im Snapshot — bitte in der Küche nachfragen.</div>`;
  }
  // Gruppieren: Speisen zuerst, Drinks danach
  const isDrink = (it) => {
    const cat = (it.categorySnapshot || it.category || '').toLowerCase();
    return cat.includes('getränk') || cat.includes('drink') || cat.includes('bier') || cat.includes('wein');
  };
  const food   = items.filter(it => !isDrink(it));
  const drinks = items.filter(it => isDrink(it));

  const renderLine = (it) => {
    const name = it.nameSnapshot || it.name || it.menu_item_name || '?';
    const qty  = Number(it.qty || 1);
    const custom = it.customizations && typeof it.customizations === 'object' ? it.customizations : {};
    let customStr = '';
    try {
      customStr = (typeof formatCustomizationsByCategory === 'function')
        ? formatCustomizationsByCategory(custom, name)
        : Object.entries(custom).filter(([_, v]) => v && (!Array.isArray(v) || v.length)).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ');
    } catch (_) { customStr = ''; }
    const noteRaw = it.notes || it.note || '';
    return `
      <div class="delivery-item-line">
        <span class="delivery-item-qty">${qty}×</span>
        <div class="delivery-item-body">
          <div class="delivery-item-name">${htmlEscape(String(name))}</div>
          ${customStr ? `<div class="delivery-item-custom">${htmlEscape(customStr)}</div>` : ''}
          ${noteRaw ? `<div class="delivery-item-note">📝 ${htmlEscape(noteRaw)}</div>` : ''}
        </div>
      </div>
    `;
  };

  return `
    <div class="delivery-items">
      ${food.length > 0 ? `
        <div class="delivery-items-group">
          <div class="delivery-items-label">🍔 Speisen (${food.reduce((s,i)=>s+Number(i.qty||1),0)})</div>
          ${food.map(renderLine).join('')}
        </div>` : ''}
      ${drinks.length > 0 ? `
        <div class="delivery-items-group">
          <div class="delivery-items-label">🥤 Getränke (${drinks.reduce((s,i)=>s+Number(i.qty||1),0)})</div>
          ${drinks.map(renderLine).join('')}
        </div>` : ''}
    </div>
  `;
}

/** Zahl-Status-Banner für den Fahrer — der wichtigste Hinweis.
 *  - Schon bezahlt (online): grünes „Bezahlt"-Häkchen
 *  - Noch offen: großer roter „KASSIEREN BEI LIEFERUNG: 24,50 €"-Banner
 *    mit Methode (cash/card) für die Vorbereitung des Wechselgelds bzw.
 *    EC-Geräts.
 */
function renderPaymentBanner(order) {
  const paid = order.paymentStatus === 'paid';
  const totalCents = Number(order.totalGrossCents || 0);
  const totalEur   = (totalCents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  if (paid) {
    const methodLabel = order.paymentMethod === 'stripe' ? 'online'
                      : order.paymentMethod === 'cash'   ? 'bar'
                      : order.paymentMethod === 'card'   ? 'Karte'
                      : 'bezahlt';
    return `
      <div class="delivery-pay delivery-pay-paid">
        <span class="delivery-pay-icon">✅</span>
        <div class="delivery-pay-body">
          <strong>Bezahlt (${htmlEscape(methodLabel)})</strong>
          <span class="delivery-pay-total">${htmlEscape(totalEur)}</span>
        </div>
      </div>
    `;
  }
  // Noch offen: prominenter Warnbanner für den Fahrer
  const methodHint = order.paymentMethod === 'cash'
                       ? 'Bar (Wechselgeld vorbereiten)'
                       : order.paymentMethod === 'card'
                         ? 'Karte (EC-Gerät mitnehmen)'
                         : 'Bar oder Karte';
  return `
    <div class="delivery-pay delivery-pay-unpaid">
      <span class="delivery-pay-icon">💶</span>
      <div class="delivery-pay-body">
        <strong>BEIM KUNDEN KASSIEREN</strong>
        <span class="delivery-pay-total">${htmlEscape(totalEur)}</span>
        <span class="delivery-pay-method">${htmlEscape(methodHint)}</span>
      </div>
    </div>
  `;
}

function fmtPickupTime(order) {
  if (!order || !order.pickupDate || !order.pickupTime) return '';
  const iso = `${order.pickupDate}T${order.pickupTime}`;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '';
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const orderDay = new Date(order.pickupDate + 'T00:00');
  let prefix = '';
  if (orderDay.getTime() === today.getTime())       prefix = 'Heute ';
  else if (orderDay.getTime() === tomorrow.getTime()) prefix = 'Morgen ';
  else prefix = orderDay.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) + ' ';
  return prefix + order.pickupTime.slice(0, 5);
}

function fmtElapsed(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const min = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (min < 1)  return 'gerade eben';
  if (min < 60) return `vor ${min} Min`;
  return `vor ${Math.floor(min / 60)}h ${min % 60} Min`;
}

function renderDriverPicker(order) {
  const current = getDriverById(order.driverId);
  // S131 Phase D — Fahrer zuweisen darf der OPERATOR (Matrix: manager + staff,
  // NICHT service). View-Gate über die Operator-Rolle; der RPC erzwingt es hart
  // (assign_driver_to_order → assert_operator_role(['manager','staff'])). Ohne
  // angemeldeten Operator (effektiv „staff") zeigen wir den Picker — die Aktion
  // löst dann das PIN-Overlay aus.
  const OR = window.OperatorRoles;
  const opApi = window.App && window.App.operator;
  const op = (opApi && typeof opApi.getOperator === 'function') ? opApi.getOperator() : null;
  const canAssign = OR ? OR.operatorCan(op, 'driver.assign') : true;

  if (!canAssign) {
    return `<div class="delivery-driver delivery-driver-readonly">
      🧑‍🍳 ${current ? htmlEscape(current.name) : '<em>noch kein Fahrer · Manager kontaktieren</em>'}
    </div>`;
  }

  if (!_drivers || _drivers.length === 0) {
    return `<div class="delivery-driver delivery-driver-readonly">
      🧑‍🍳 <em>Keine aktiven Fahrer angelegt — </em>
      <a href="#" onclick="event.preventDefault(); switchView('admin'); setTimeout(()=>switchTab('settings'),50);" style="color:var(--accent);">
        unter System → Team (Rolle „Fahrer") anlegen
      </a>
    </div>`;
  }

  const options = [`<option value="">— Fahrer wählen —</option>`]
    .concat(_drivers.map(d => {
      const sel = d.id === order.driverId ? 'selected' : '';
      // S209/S212 — abgeleitete Auslastung aus busy_count (offene Orders): „(N offen)".
      const busy = Number(d.busy_count) || 0;
      const statusBadge = busy > 0 ? ` 🟡 (${busy} offen)` : '';
      return `<option value="${htmlEscape(d.id)}" ${sel}>${htmlEscape(d.name)}${statusBadge}</option>`;
    }));

  return `
    <div class="delivery-driver">
      <label class="delivery-driver-label">🧑‍🍳 Fahrer</label>
      <select class="delivery-driver-select"
              onchange="deliveryAssignDriver('${htmlEscape(order.id)}', this.value)">
        ${options.join('')}
      </select>
    </div>
  `;
}

function renderTile(order, nowMs = Date.now()) {
  const bucket    = bucketOf(order);
  const next      = NEXT_STATUS[bucket];
  const pre       = isPreOrder(order, nowMs);
  const pickupTxt = fmtPickupTime(order);
  const customer  = order.customerName || 'Kunde';
  const phone     = order.customerPhone || '';
  const items     = itemsForRender(order);
  const lastEvent = order.deliveryOutForDeliveryAt || order.deliveryPickedUpAt || order.deliveryAssignedAt || order.createdAt;
  const elapsed   = fmtElapsed(lastEvent);
  const special   = (order.specialRequests || '').trim();
  const orderNotes = (order.notes || '').trim();

  // S62 HF7 — IMMER „Next"-Button anzeigen. Bei out_for_delivery ist die
  // Aktion „✅ Ausgeliefert + Bezahlt", die die Kachel räumt (S98). Bei
  // pending+kein Driver: Klick triggert Force-Driver-UX (Toast + Highlight).
  const showNext = !!next;

  // S62 HF11 — Küche+Theke-Ready-Signal mit Ampel. Master-Status='ready'
  // aggregiert bedeutet: beide Kanäle sind fertig. Dann wartet die Order
  // jetzt nur noch auf den Fahrer-Pickup → prominenter Banner + Ampel-Color
  // basierend auf Wartezeit seit readyAt (grün<5min, gelb<10min, rot>10min).
  const isReadyForPickup = bucket === 'assigned' && order.status === 'ready';
  let readyAmpelClass = '';
  let readyMinutesLabel = '';
  if (isReadyForPickup && order.readyAt) {
    const readyMs = new Date(order.readyAt).getTime();
    const minsSinceReady = Math.max(0, Math.floor((nowMs - readyMs) / 60000));
    readyMinutesLabel = minsSinceReady === 0 ? 'gerade fertig' : `seit ${minsSinceReady} Min fertig`;
    if      (minsSinceReady < 5)  readyAmpelClass = 'ampel-green';
    else if (minsSinceReady < 10) readyAmpelClass = 'ampel-yellow';
    else                          readyAmpelClass = 'ampel-red';
  } else if (isReadyForPickup) {
    readyMinutesLabel = 'gerade fertig';
    readyAmpelClass = 'ampel-green';
  }

  // S71 DELIV-GATE — Auslieferungs-Gate: zugewiesene Order, deren Küche+Theke
  // noch NICHT fertig sind, dürfen NICHT in Auslieferung gehen. Vor S71 war
  // der Button immer klickbar (nur visuelle Ampel als Hinweis) — Fahrer konnte
  // ungekochte Burger mitnehmen. Status-Single-Source ist order.status === 'ready'
  // aus core/order-statuses.js.
  const isAssignedNotReady = bucket === 'assigned' && !isReadyForPickup;

  // S71 DELIV-DRIVER-GATE — pending-Order ohne ausgewählten Fahrer: Next-Button
  // disabled bis Dropdown bedient ist. Vor S71 war der Button klickbar mit
  // Warn-Toast (S62 HF7 Force-Driver-UX) — jetzt analog zu DELIV-GATE hartes
  // UI-Gate. Handler-Side-Guard (Z688ff) bleibt als Defense-in-Depth.
  const isPendingNoDriver = bucket === 'pending' && !order.driverId;

  // S62 HF11.2 — wenn „scharf" (Küche+Theke fertig), wandert der primäre
  // Tile-Status-Indikator (Border-Top-Color) von „assigned"-blau auf die
  // jeweilige Ampel-Color um. Konsistenz: das ganze Tile signalisiert den
  // Dringlichkeits-Zustand, nicht nur der innere Banner.
  const tileClasses = [
    'delivery-tile',
    pre ? 'is-preorder' : '',
    `bucket-${bucket}`,
    isReadyForPickup ? `is-ready-for-pickup ${readyAmpelClass}` : ''
  ].filter(Boolean).join(' ');

  return `
    <article class="${tileClasses}"
             data-id="${htmlEscape(order.id)}">
      <!-- Kopf: Bestellnummer · Pickup-Zeit -->
      <header class="delivery-tile-head">
        <div class="delivery-tile-head-left">
          <span class="delivery-tile-number">#${htmlEscape(order.orderNumber || order.id.slice(-6))}</span>
          ${renderOperatorBadges(order)}
          ${pre
            ? `<span class="delivery-tile-pre">📅 Pre-Order · ${htmlEscape(pickupTxt)}</span>`
            : `<span class="delivery-tile-now">🛵 Liefern: ${htmlEscape(pickupTxt || 'jetzt')}</span>`
          }
        </div>
        ${elapsed ? `<span class="delivery-tile-elapsed">${htmlEscape(elapsed)}</span>` : ''}
      </header>

      <!-- 1) ZAHL-STATUS — wichtigster Hinweis, kommt direkt nach dem Kopf -->
      ${renderPaymentBanner(order)}

      <!-- S212 — Fehlgeschlagene Zustellung: Fahrer-Meldung + Grund. Manager
           handelt hier: neu zuweisen (Dropdown) hebt die Markierung auf, oder
           Abbrechen (bei prepaid Korrekturbeleg über die Korrektur-Engine). -->
      ${order.deliveryFailedAt ? `
        <div class="delivery-tile-failed">
          ✖ Zustellung fehlgeschlagen${order.deliveryFailedReason ? ` — ${htmlEscape(order.deliveryFailedReason)}` : ''}
          <div class="delivery-tile-failed-sub">Neu zuweisen oder abbrechen${order.paymentStatus === 'paid' ? ' · bereits bezahlt → ggf. Korrekturbeleg' : ''}</div>
        </div>
      ` : ''}

      <!-- 2) KUNDE — Name + Telefon prominent für Anruf bei Ankunft -->
      <div class="delivery-tile-customer">
        <div class="delivery-tile-customer-name"><strong>${htmlEscape(customer)}</strong></div>
        ${phone ? `<a href="tel:${htmlEscape(phone)}" class="delivery-tile-phone" onclick="event.stopPropagation()">📞 ${htmlEscape(phone)}</a>` : '<span class="delivery-tile-phone delivery-tile-phone-missing">⚠️ Keine Tel.-Nr.</span>'}
      </div>

      <!-- 3) ZIELADRESSE — voll mit Etage, Klingel, Hinweis und Maps-Link -->
      <div class="delivery-tile-section delivery-tile-where">
        <div class="delivery-tile-section-label">📍 ZIEL-ADRESSE</div>
        ${fmtAddress(order.deliveryAddress, customer)}
      </div>

      <!-- 4) ITEMS — Speisen + Drinks getrennt, Customizations sichtbar, fürs Mit-Picken -->
      <div class="delivery-tile-section delivery-tile-what">
        <div class="delivery-tile-section-label">📦 ZU LIEFERN
          <span class="delivery-tile-section-count">${items.reduce((s,it)=>s+Number(it.qty||1),0)} Pos.</span>
        </div>
        ${fmtItems(items)}
      </div>

      <!-- 5) SONDERWÜNSCHE / NOTIZEN — falls vorhanden -->
      ${(special || orderNotes) ? `
        <div class="delivery-tile-section delivery-tile-special">
          <div class="delivery-tile-section-label">⚠️ SONDERWÜNSCHE</div>
          ${special     ? `<div class="delivery-tile-special-line">${htmlEscape(special)}</div>` : ''}
          ${orderNotes  ? `<div class="delivery-tile-special-line">${htmlEscape(orderNotes)}</div>` : ''}
        </div>
      ` : ''}

      <!-- 6) FAHRER-ZUWEISUNG -->
      ${renderDriverPicker(order)}

      <!-- S62 HF11 — Küche+Theke-Fertig-Banner mit Ampel-Color, sobald
           der Master-Status 'ready' erreicht ist und die Order auf den
           Fahrer wartet. Liefer-spezifische 5/10-Min-Ampel (Liefer-Fenster
           ist enger als KDS-Schwellen). -->
      ${isReadyForPickup ? `
        <div class="delivery-tile-ready ${readyAmpelClass}">
          <div class="delivery-tile-ready-head">
            🟢 KÜCHE + THEKE FERTIG
            <span class="delivery-tile-ready-time">${htmlEscape(readyMinutesLabel)}</span>
          </div>
          <div class="delivery-tile-ready-sub">Fahrer kann losfahren — bitte zügig auf Tour</div>
        </div>
      ` : ''}

      <!-- 7) STATUS + AKTIONEN — S212: der Fahrer treibt den Status jetzt im
           Terminal (kassiert + zugestellt). Das Cockpit zeigt den Status nur noch
           read-only (live via Realtime) und behält Zuweisung (oben) + Storno. -->
      <footer class="delivery-tile-foot">
        <span class="delivery-status-ro delivery-status-ro-${bucket}">
          ${htmlEscape(_statusRoLabel(bucket))}
        </span>
        ${bucket !== 'pending' ? `
          <button class="btn-delivery-cancel" onclick="deliveryCancelDelivery('${htmlEscape(order.id)}')"
                  title="Lieferung abbrechen (z.B. Kunde nicht erreichbar)">
            ✕ Abbrechen
          </button>
        ` : ''}
      </footer>
    </article>
  `;
}

/** S212 — Read-only-Status-Label fürs Cockpit (kein Fortschritts-Button mehr). */
function _statusRoLabel(bucket) {
  if (bucket === 'pending')          return '📥 Neu — Fahrer zuweisen';
  if (bucket === 'assigned')         return '🧑‍🍳 Zugewiesen — wartet auf „Unterwegs" (Fahrer)';
  if (bucket === 'out_for_delivery') return '🛵 In Auslieferung (Fahrer)';
  return bucket || '';
}

// -----------------------------------------------------------------------------
// Haupt-Renderer
// -----------------------------------------------------------------------------

let _showAllPickupDates = false;

export function renderDelivery() {
  const root = document.getElementById('view-delivery');
  if (!root) return;

  const orders = getDeliveryOrders();
  const counts = getDeliveryCounts(orders);

  // Falls keine Settings.delivery on → Hint-Bildschirm
  const settings = getState().settings || {};
  const deliveryCfg = (settings && settings.delivery) || {};
  const deliveryEnabled = !!(deliveryCfg.internal_drivers_enabled || deliveryCfg.uber_marketplace_enabled);

  if (!deliveryEnabled) {
    root.innerHTML = `
      <div class="delivery-topbar">
        <div class="delivery-brand">
          <span class="delivery-brand-mark">G&amp;G</span>
          <span>Liefer-Cockpit</span>
        </div>
        <div class="delivery-actions">
          <button class="btn-delivery-ghost" onclick="switchView('admin')">← Cockpit</button>
        </div>
      </div>
      <div class="delivery-empty">
        <div class="delivery-empty-icon">🛵</div>
        <div class="delivery-empty-text">Lieferungen sind in deinen Einstellungen noch nicht aktiv.</div>
        <div class="delivery-empty-hint">Aktiviere „Eigene Fahrer" oder „Uber Marketplace" unter <strong>Einstellungen → Lieferung</strong>.</div>
        <button class="btn-delivery-ghost" style="margin-top:18px;" onclick="switchView('admin'); setTimeout(()=>switchTab('settings'),50);">
          Einstellungen öffnen
        </button>
      </div>
    `;
    return;
  }

  // S62 HF9 — Flaches Tile-Grid analog KDS/Theke (Jonas-Direktive). Phasen-
  // Übersicht läuft über die Counter-Pills in der Topbar + die Border-Color
  // pro Tile. Sortierung 1:1 wie KDS:
  //   1. pickup_time absteigend (späteste links — neueste Wartezeit unten)
  //   2. Bucket-Rank als Tiebreaker (weiter in der Pipeline rückt nach rechts)
  //   3. createdAt absteigend (älteste Order rechts)
  const BUCKET_RANK = { pending: 0, assigned: 1, out_for_delivery: 2 };
  const sorted = orders.slice().sort((a, b) => {
    const aKey = String(a.pickupDate || '') + ' ' + String(a.pickupTime || '00:00');
    const bKey = String(b.pickupDate || '') + ' ' + String(b.pickupTime || '00:00');
    if (aKey !== bKey) return bKey.localeCompare(aKey);
    const r = (BUCKET_RANK[bucketOf(a)] || 0) - (BUCKET_RANK[bucketOf(b)] || 0);
    if (r !== 0) return r;
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
  const gridHtml = sorted.map(o => renderTile(o)).join('');
  const noOrders = orders.length === 0;

  root.innerHTML = `
    <div class="delivery-topbar">
      <div class="delivery-brand">
        <span class="delivery-brand-mark">G&amp;G</span>
        <span>Liefer-Cockpit</span>
      </div>
      <div class="delivery-counts">
        <span class="delivery-count-pill bucket-pending">Neu: ${counts.pending || 0}</span>
        <span class="delivery-count-pill bucket-assigned">Zugewiesen: ${counts.assigned || 0}</span>
        <span class="delivery-count-pill bucket-out_for_delivery">In Auslieferung: ${counts.out_for_delivery || 0}</span>
        ${counts.preOrder > 0 ? `<span class="delivery-count-pill is-preorder">📅 Pre: ${counts.preOrder}</span>` : ''}
      </div>
      <div class="delivery-actions">
        <button class="btn-delivery-ghost${_mapOpen ? ' is-active' : ''}" onclick="deliveryToggleMap()" title="Fahrer-Karte (Live-Standorte)">🗺 Karte</button>
        <button class="btn-delivery-ghost" onclick="deliveryToggleFullscreen()" title="Vollbild">⛶</button>
        <button class="btn-delivery-ghost" onclick="switchView('admin')">← Cockpit</button>
      </div>
    </div>
    ${noOrders ? `
      <div class="delivery-empty">
        <div class="delivery-empty-icon">🛵</div>
        <div class="delivery-empty-text">Keine offenen Lieferungen.</div>
        <div class="delivery-empty-hint">Sobald eine Liefer-Bestellung reinkommt, taucht sie hier auf.</div>
      </div>
    ` : `<div class="delivery-grid">${gridHtml}</div>`}
  `;
}

// -----------------------------------------------------------------------------
// Aktionen
// -----------------------------------------------------------------------------

export async function deliveryAssignDriver(orderId, driverId) {
  if (!orderId) return;
  if (!driverId) return; // leere Auswahl ignorieren
  try {
    const storage = window.App && window.App.storage && window.App.storage.orders;
    if (!storage || typeof storage.assignDriver !== 'function') {
      throw new Error('assignDriver-API nicht verfügbar');
    }
    const updated = await storage.assignDriver(orderId, driverId);
    if (updated && window.App.events) {
      try { await window.App.events.emit('order:status_changed', { order: updated }); } catch (_) {}
      // S62 HF10 — separates Event für KDS/Theke: „Fahrer wartet, jetzt scharf"
      try { await window.App.events.emit('order:delivery_assigned', { order: updated }); } catch (_) {}
    }
    showToast(`Fahrer zugewiesen — Bestellung ${updated && updated.orderNumber ? '#' + updated.orderNumber : ''}`, 'success');
    renderDelivery();
  } catch (e) {
    console.error('[delivery] assignDriver:', e);
    showToast('Zuweisung fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function deliveryCancelDelivery(orderId) {
  if (!orderId) return;
  const ok = (await window.styledConfirm({ title: 'Diese Lieferung abbrechen?', body: 'Die Bestellung selbst bleibt erhalten. Nur der Liefer-Workflow wird gestoppt.', danger: true, confirmLabel: 'Abbrechen' })).confirmed;
  if (!ok) return;
  try {
    const storage = window.App && window.App.storage && window.App.storage.orders;
    const updated = await storage.updateDeliveryStatus(orderId, 'cancelled');
    if (updated && window.App.events) {
      try { await window.App.events.emit('order:status_changed', { order: updated }); } catch (_) {}
    }
    showToast('Lieferung abgebrochen.', 'success');
    renderDelivery();
  } catch (e) {
    console.error('[delivery] cancel:', e);
    showToast('Abbruch fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export function deliveryToggleFullscreen() {
  try {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      if (el.requestFullscreen) el.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  } catch (_) { /* ignore */ }
}

// -----------------------------------------------------------------------------
// View-Lifecycle
// -----------------------------------------------------------------------------

let _deliveryInitialized = false;
let _deliveryTickerInterval = null;

// -----------------------------------------------------------------------------
// S210 — Fahrer-Live-Karte (Leaflet/OSM). Einklappbares Body-Panel, das die
// renderDelivery-Full-Renders überlebt (deshalb an document.body, nicht in
// #view-delivery). Realtime bewegt NUR die Marker (kein Karten-Rebuild),
// Fallback-Poll 30 s. Leaflet wird lazy aus public/vendor geladen (kein Import).
// -----------------------------------------------------------------------------
const DL_MAP_LS_KEY     = 'gg.delivery.mapOpen';
const DL_MAP_CENTER_KEY = 'gg.delivery.mapCenter';
const DL_FALLBACK_CENTER = [53.5511, 9.9937];   // Hamburg-City
const DL_LEAFLET_JS  = 'vendor/leaflet-1.9.4.js';
const DL_LEAFLET_CSS = 'vendor/leaflet-1.9.4.css';

let _mapOpen = (typeof localStorage !== 'undefined' && localStorage.getItem(DL_MAP_LS_KEY) === '1');
let _map = null;
let _mapPanel = null;
let _markers = {};        // user_id -> { marker, updatedMs }
let _driverInfo = {};     // user_id -> { name, initials, busy_count } (aus RPC-Join)
let _mapPollTimer = null;
let _mapChannel = null;
let _leafletPromise = null;
let _mapDidFit = false;

/** Pure (Vitest): Frische-Bucket eines Standort-Timestamps (grün <2min · gelb
 *  <5min · grau älter). */
export function locationFreshnessBucket(updatedAtMs, nowMs) {
  const age = nowMs - updatedAtMs;
  if (age < 2 * 60 * 1000) return 'fresh';
  if (age < 5 * 60 * 1000) return 'stale';
  return 'old';
}

/** Pure (Vitest): kurze relative Alters-Angabe („vor X s" / „vor X Min"). */
export function relativeAgeShort(updatedAtMs, nowMs) {
  const s = Math.max(0, Math.round((nowMs - updatedAtMs) / 1000));
  if (s < 60) return `vor ${s} s`;
  return `vor ${Math.floor(s / 60)} Min`;
}

function _savedCenter() {
  try {
    const raw = localStorage.getItem(DL_MAP_CENTER_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    return (Array.isArray(c) && c.length === 2 && isFinite(c[0]) && isFinite(c[1])) ? c : null;
  } catch (_) { return null; }
}
function _saveCenter() {
  try {
    if (!_map) return;
    const c = _map.getCenter();
    localStorage.setItem(DL_MAP_CENTER_KEY, JSON.stringify([c.lat, c.lng]));
  } catch (_) { /* ignore */ }
}

/** Leaflet lazy aus public/vendor laden (kein Vite-Import → Build/Vitest ohne
 *  Datei; Datei kommt via scripts/vendor-cdn.sh). Cached Promise. */
function _loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (_leafletPromise) return _leafletPromise;
  _leafletPromise = new Promise((resolve, reject) => {
    try {
      if (!document.querySelector('link[data-leaflet]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet'; link.href = DL_LEAFLET_CSS; link.setAttribute('data-leaflet', '1');
        document.head.appendChild(link);
      }
      const s = document.createElement('script');
      s.src = DL_LEAFLET_JS; s.async = true; s.setAttribute('data-leaflet', '1');
      s.onload = () => window.L ? resolve(window.L) : reject(new Error('Leaflet nicht geladen'));
      s.onerror = () => reject(new Error('Vendor-Datei fehlt — scripts/vendor-cdn.sh ausführen'));
      document.body.appendChild(s);
    } catch (e) { reject(e); }
  });
  return _leafletPromise;
}

/** Toggle „🗺 Karte" — Panel ein-/ausklappen, Zustand in localStorage. */
export function deliveryToggleMap() {
  _mapOpen = !_mapOpen;
  try { localStorage.setItem(DL_MAP_LS_KEY, _mapOpen ? '1' : '0'); } catch (_) { /* ignore */ }
  _updateMapPanel();
  const btn = document.querySelector('.delivery-actions .btn-delivery-ghost[onclick*="deliveryToggleMap"]');
  if (btn) btn.classList.toggle('is-active', _mapOpen);
}

function _updateMapPanel() {
  const active = (typeof document !== 'undefined') && document.body.classList.contains('delivery-active');
  if (_mapOpen && active) _ensureMapPanel();
  else _hideMapPanel();
}

function _ensureMapPanel() {
  if (_mapPanel) {
    _mapPanel.style.display = '';
    _startMapLive();
    if (_map) setTimeout(() => { try { _map.invalidateSize(); } catch (_) {} }, 60);
    return;
  }
  const panel = document.createElement('div');
  panel.className = 'delivery-map-panel';
  panel.innerHTML = `
    <div class="dl-map-head">
      <span class="dl-map-title">🗺 Fahrer-Karte</span>
      <button type="button" class="dl-map-close" onclick="deliveryToggleMap()" aria-label="Karte schließen">×</button>
    </div>
    <div class="dl-map-canvas" id="delivery-map-canvas"></div>
    <div class="dl-map-foot" id="delivery-map-foot">Karte lädt …</div>`;
  document.body.appendChild(panel);
  _mapPanel = panel;

  _loadLeaflet().then((L) => {
    const canvas = panel.querySelector('#delivery-map-canvas');
    _map = L.map(canvas, { zoomControl: true, attributionControl: true })
      .setView(_savedCenter() || DL_FALLBACK_CENTER, 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '© OpenStreetMap-Mitwirkende'
    }).addTo(_map);
    _map.on('moveend', _saveCenter);
    _startMapLive();
  }).catch((e) => {
    const foot = panel.querySelector('#delivery-map-foot');
    if (foot) foot.textContent = 'Karte nicht verfügbar: ' + (e && (e.message || e));
    console.warn('[delivery] leaflet load:', e);
  });
}

function _hideMapPanel() {
  if (_mapPanel) _mapPanel.style.display = 'none';
  _stopMapLive();
}

function _startMapLive() {
  _refreshMapLocations();
  if (!_mapPollTimer) {
    _mapPollTimer = setInterval(() => {
      if (_mapOpen && document.body.classList.contains('delivery-active')) _refreshMapLocations();
    }, 30000);
  }
  if (!_mapChannel) _subscribeMapRealtime();
}

function _stopMapLive() {
  if (_mapPollTimer) { clearInterval(_mapPollTimer); _mapPollTimer = null; }
  if (_mapChannel) {
    try { window.App.supabase.removeChannel(_mapChannel); } catch (_) { /* ignore */ }
    _mapChannel = null;
  }
}

function _subscribeMapRealtime() {
  const supa = window.App && window.App.supabase;
  if (!supa || typeof supa.channel !== 'function') return;
  try {
    _mapChannel = supa.channel('gg-driver-locations')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'driver_locations' }, (payload) => {
        if (!_map) return;
        const row = payload && (payload.new || payload.old);
        if (!row || !row.user_id) return;
        if (payload.eventType === 'DELETE') { _removeMarker(row.user_id); return; }
        // Name/Initialen kommen aus dem RPC-Join; unbekannt → Refresh anstoßen,
        // Marker aber sofort bewegen (nur bewegen, kein Karten-Rebuild).
        if (!_driverInfo[row.user_id]) _refreshMapLocations();
        _placeMarker(row.user_id, row.lat, row.lng, row.updated_at);
      })
      .subscribe();
  } catch (e) { console.warn('[delivery] driver_locations realtime:', e); }
}

function _refreshMapLocations() {
  const storage = window.App && window.App.storage && window.App.storage.driverLocations;
  if (!storage || typeof storage.list !== 'function' || !_map) return;
  storage.list().then((rows) => {
    if (!_map) return;
    const seen = new Set();
    (rows || []).forEach((r) => {
      _driverInfo[r.user_id] = { name: r.name, initials: r.initials, busy_count: r.busy_count };
      _placeMarker(r.user_id, r.lat, r.lng, r.updated_at);
      seen.add(r.user_id);
    });
    // Fahrer, die nicht mehr frisch sind (>10 min → RPC liefert sie nicht), raus.
    Object.keys(_markers).forEach((uid) => { if (!seen.has(uid)) _removeMarker(uid); });
    _updateMapFoot(seen.size);
    _maybeFit();
  }).catch((e) => { console.warn('[delivery] list_driver_locations:', e); });
}

function _placeMarker(uid, lat, lng, updatedAt) {
  const L = window.L;
  if (!_map || !L || typeof lat !== 'number' || typeof lng !== 'number') return;
  const updatedMs = updatedAt ? new Date(updatedAt).getTime() : Date.now();
  const info = _driverInfo[uid] || {};
  const bucket = locationFreshnessBucket(updatedMs, Date.now());
  const initials = info.initials || (info.name ? info.name.slice(0, 2).toUpperCase() : '?');
  const tip = `${info.name || 'Fahrer'} · ${relativeAgeShort(updatedMs, Date.now())}`
            + (info.busy_count ? ` · ${info.busy_count} Lieferung${info.busy_count === 1 ? '' : 'en'}` : '');
  const icon = L.divIcon({
    className: '', iconSize: [34, 34], iconAnchor: [17, 17],
    html: `<span class="dl-marker dl-marker-${bucket}">${htmlEscape(initials)}</span>`
  });
  let entry = _markers[uid];
  if (!entry) {
    const marker = L.marker([lat, lng], { icon }).addTo(_map);
    marker.bindTooltip(tip);
    _markers[uid] = { marker, updatedMs };
  } else {
    entry.marker.setLatLng([lat, lng]);   // nur bewegen
    entry.marker.setIcon(icon);
    entry.marker.setTooltipContent(tip);
    entry.updatedMs = updatedMs;
  }
}

function _removeMarker(uid) {
  const entry = _markers[uid];
  if (entry && _map) { try { _map.removeLayer(entry.marker); } catch (_) {} }
  delete _markers[uid];
}

function _maybeFit() {
  if (_mapDidFit || !_map || !window.L) return;
  const ids = Object.keys(_markers);
  if (ids.length === 0) return;
  try {
    const bounds = window.L.latLngBounds(ids.map((id) => _markers[id].marker.getLatLng()));
    _map.fitBounds(bounds, { maxZoom: 15, padding: [40, 40] });
    _mapDidFit = true;
  } catch (_) { /* ignore */ }
}

function _updateMapFoot(n) {
  const foot = _mapPanel && _mapPanel.querySelector('#delivery-map-foot');
  if (!foot) return;
  foot.textContent = n > 0 ? `${n} Fahrer live` : 'Keine aktiven Fahrer-Standorte.';
}

export async function enterDeliveryView() {
  document.body.classList.add('delivery-active');
  // Drivers laden, dann rendern (damit der Picker direkt befüllt ist)
  await loadDrivers(true);
  renderDelivery();
  installDeliveryListenersOnce();
  // Pre-Fetch: alle aktiven Delivery-Order-Items vorab laden, damit der
  // Fahrer-Tile beim ersten Render keine „Items lazy nachladen"-Lücke zeigt.
  try {
    const storage = window.App && window.App.storage && window.App.storage.orders;
    if (storage && typeof storage.listItems === 'function') {
      const orderIds = getDeliveryOrders()
        .map(o => String(o.id || ''))
        .filter(Boolean)
        .filter(id => !hasCachedItems(id));
      if (orderIds.length > 0) {
        // S104-B: EIN Batch-Request statt N (gemeinsames Modul).
        await batchFetchItemsFor(orderIds);
        if (document.body.classList.contains('delivery-active')) renderDelivery();
      }
    }
  } catch (e) { console.warn('[delivery] pre-fetch items failed:', e); }
  // Hash sauber auf '#delivery' setzen
  try {
    if (window.location.hash !== '#delivery') {
      const url = window.location.pathname + window.location.search + '#delivery';
      window.history.replaceState(null, '', url);
    }
  } catch (_) { /* sandboxed Browser */ }
  // Ticker: alle 30s neu rendern, damit „vor X Min" mitwandert + Pre-Order-Flips greifen.
  if (_deliveryTickerInterval) clearInterval(_deliveryTickerInterval);
  _deliveryTickerInterval = setInterval(() => {
    if (document.body.classList.contains('delivery-active')) renderDelivery();
  }, 30000);
  // S210 — gemerkten Karten-Zustand wiederherstellen (Panel + Live starten).
  _updateMapPanel();
}

export function exitDeliveryView() {
  document.body.classList.remove('delivery-active');
  if (_deliveryTickerInterval) {
    clearInterval(_deliveryTickerInterval);
    _deliveryTickerInterval = null;
  }
  // S210 — Karte verstecken + Live (Poll/Realtime) stoppen (Panel-Instanz bleibt
  // erhalten fürs Wieder-Öffnen). Ansonsten liefe der Realtime-Channel weiter.
  _hideMapPanel();
  // Hash aufräumen
  try {
    if (window.location.hash.includes('#delivery')) {
      const cleaned = window.location.hash.replace(/#delivery/g, '');
      const url = window.location.pathname + window.location.search + cleaned;
      window.history.replaceState(null, '', url);
    }
  } catch (_) { /* sandboxed Browser */ }
}

function installDeliveryListenersOnce() {
  if (_deliveryInitialized) return;
  _deliveryInitialized = true;
  const App = window.App;
  if (!App || !App.events) return;

  // S60 PERF.3-Pattern — Debouncing für Re-Renders
  let _renderTimer = null;
  function scheduleRender() {
    if (!document.body.classList.contains('delivery-active')) return;
    if (_renderTimer) return;
    _renderTimer = setTimeout(() => {
      _renderTimer = null;
      if (document.body.classList.contains('delivery-active')) renderDelivery();
    }, 150);
  }

  App.events.on('order:realtime',         scheduleRender);
  // S62 HF11 — wenn Küche+Theke fertig sind (status:'ready') und es eine
  // Liefer-Order ist, gibt das Cockpit einen Ping ab + Re-Render mit dem
  // neuen „Küche+Theke fertig"-Banner. Disponent merkt's auch ohne Hingucken.
  App.events.on('order:status_changed', payload => {
    const o = payload && payload.order;
    if (o && o.serviceType === 'delivery' && o.status === 'ready'
        && document.body.classList.contains('delivery-active')
        && typeof window.playOrderPing === 'function') {
      try { window.playOrderPing(); } catch (_) {}
    }
    scheduleRender();
  });
  App.events.on('order:created',          scheduleRender);
  App.events.on('order:updated',          scheduleRender);
  App.events.on('view:became-visible',    (p) => {
    if (p && p.view === 'delivery') {
      loadDrivers(true).then(scheduleRender);
    }
  });
  // Drivers-Cache neu laden bei Driver-Änderungen (kommt vom Team-Sub-Tab)
  App.events.on('drivers:changed',        () => loadDrivers(true).then(scheduleRender));
  // S209 — list_assignable_drivers ist operator-gegated (manager/staff). Meldet
  // sich erst nach dem Cockpit-Render ein Operator an, muss der Dropdown frisch
  // nachladen (sonst bleibt er leer bis zum nächsten TTL-Ablauf).
  App.events.on('operator:changed',       () => loadDrivers(true).then(scheduleRender));
}

// -----------------------------------------------------------------------------
// Bridge auf window — für Inline-onclick-Handler (Memory feedback_gg_inline_handler_csp)
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderDelivery,
    enterDeliveryView,
    exitDeliveryView,
    deliveryAssignDriver,
    deliveryCancelDelivery,
    deliveryToggleFullscreen,
    deliveryToggleMap,        // S210 — Fahrer-Live-Karte
    getDeliveryCounts
  });
}
